import { validateTronAddressFormat } from './tronAddress';
import { formatNetworkLabel } from './networkLabel';

export interface PoolNetworkOption {
  value: string;
  label: string;
}

export interface PoolCurrencyConfig {
  currency: string;
  networks: PoolNetworkOption[];
  validateAddress: (address: string) => string | null;
  addressLabel: string;
  placeholder: string;
  hint: string;
}

const networkOption = (value: string): PoolNetworkOption => ({
  value,
  label: formatNetworkLabel(value),
});

/** Client-side EVM (Ethereum) address format check. Server validates fully. */
export function validateEvmAddressFormat(address: string): string | null {
  const trimmed = address.trim();
  if (!trimmed) return 'Address is required';
  if (/\s/.test(trimmed)) return 'Address must not contain whitespace';
  if (!/^0x[0-9a-fA-F]{40}$/.test(trimmed)) {
    return 'Ethereum address must be 0x followed by 40 hexadecimal characters';
  }
  return null;
}

/**
 * Client-side Bitcoin address format check (legacy or Bech32). Testnet/signet
 * forms (m…/n…/2…, tb1…) pass too; the server verifies the checksum and that
 * the address belongs to the network accounting is configured for.
 */
export function validateBtcAddressFormat(address: string): string | null {
  const trimmed = address.trim();
  if (!trimmed) return 'Address is required';
  if (/\s/.test(trimmed)) return 'Address must not contain whitespace';
  const legacy = /^[123mn][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(trimmed);
  const bech32 = /^(bc|tb)1[ac-hj-np-z02-9]{11,87}$/i.test(trimmed);
  if (!legacy && !bech32) {
    return 'Bitcoin address must be a valid legacy (1…/3…) or Bech32 (bc1…) address; testnet: m…/n…/2…/tb1…';
  }
  return null;
}

/**
 * Currencies the backoffice can add to the deposit pool, with their networks
 * and per-currency address validation. Mirrors the accounting
 * WalletAddressValidator so the UI fails fast before hitting the API.
 */
export const POOL_CURRENCY_CONFIGS: PoolCurrencyConfig[] = [
  {
    currency: 'USDT',
    networks: [networkOption('TRC20')],
    validateAddress: validateTronAddressFormat,
    addressLabel: 'TRON address',
    placeholder: 'T…',
    hint: '34-character Base58 address (starts with T). Checksum is verified on the server.',
  },
  {
    currency: 'ETH',
    networks: [networkOption('ETH')],
    validateAddress: validateEvmAddressFormat,
    addressLabel: 'Ethereum address',
    placeholder: '0x…',
    hint: '0x followed by 40 hexadecimal characters.',
  },
  {
    currency: 'BTC',
    networks: [networkOption('BTC')],
    validateAddress: validateBtcAddressFormat,
    addressLabel: 'Bitcoin address',
    placeholder: 'bc1… / 1… / 3…',
    hint: 'Legacy (1…/3…) or Bech32 (bc1…) address.',
  },
  {
    currency: 'TRX',
    networks: [networkOption('TRON')],
    validateAddress: validateTronAddressFormat,
    addressLabel: 'TRON address',
    placeholder: 'T…',
    hint: '34-character Base58 address (starts with T). Checksum is verified on the server.',
  },
];

export const POOL_CURRENCIES: string[] = POOL_CURRENCY_CONFIGS.map((c) => c.currency);

export function getPoolCurrencyConfig(currency: string): PoolCurrencyConfig {
  const upper = (currency || '').toUpperCase();
  return POOL_CURRENCY_CONFIGS.find((c) => c.currency === upper) ?? POOL_CURRENCY_CONFIGS[0];
}
